/**
 * Lightweight, dependency-free depth effect for the Hero section only.
 * Desktop/pointer-capable devices: subtle pointer-driven translate on the media layer.
 * Touch devices: subtle scroll-driven translate on the media layer, only while in view.
 * Fully inert when prefers-reduced-motion is set, or when this script never runs.
 */
class HeroParallax extends HTMLElement {
  connectedCallback() {
    this.desktopMedia = this.querySelector('.hero__media--desktop');
    this.mobileMedia = this.querySelector('.hero__media--mobile');
    this.ticking = false;

    this.reducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.pointerQuery = window.matchMedia('(hover: hover) and (pointer: fine)');

    this.onReducedMotionChange = () => this.evaluate();
    this.onPointerQueryChange = () => this.evaluate();
    this.reducedMotionQuery.addEventListener('change', this.onReducedMotionChange);
    this.pointerQuery.addEventListener('change', this.onPointerQueryChange);

    this.evaluate();
  }

  disconnectedCallback() {
    this.reducedMotionQuery.removeEventListener('change', this.onReducedMotionChange);
    this.pointerQuery.removeEventListener('change', this.onPointerQueryChange);
    this.teardown();
  }

  evaluate() {
    this.teardown();

    if (this.reducedMotionQuery.matches) return;

    if (this.pointerQuery.matches) {
      this.setupPointer();
    } else {
      this.setupScroll();
    }
  }

  teardown() {
    if (this.onPointerMove) this.removeEventListener('pointermove', this.onPointerMove);
    if (this.onPointerLeave) this.removeEventListener('pointerleave', this.onPointerLeave);
    if (this.onScroll) window.removeEventListener('scroll', this.onScroll);
    if (this.observer) this.observer.disconnect();

    this.onPointerMove = null;
    this.onPointerLeave = null;
    this.onScroll = null;
    this.observer = null;

    [this.desktopMedia, this.mobileMedia].forEach((el) => {
      if (!el) return;
      el.classList.remove('hero-parallax__media--active');
      el.style.removeProperty('--hero-parallax-x');
      el.style.removeProperty('--hero-parallax-y');
    });
  }

  setupPointer() {
    if (!this.desktopMedia) return;
    this.desktopMedia.classList.add('hero-parallax__media--active');

    const maxTranslate = 5; // px, restrained on purpose

    this.onPointerMove = (event) => {
      this.pointerX = event.clientX;
      this.pointerY = event.clientY;
      this.queuePointerUpdate(maxTranslate);
    };

    this.onPointerLeave = () => {
      this.desktopMedia.style.setProperty('--hero-parallax-x', '0px');
      this.desktopMedia.style.setProperty('--hero-parallax-y', '0px');
    };

    this.addEventListener('pointermove', this.onPointerMove);
    this.addEventListener('pointerleave', this.onPointerLeave);
  }

  queuePointerUpdate(maxTranslate) {
    if (this.ticking) return;
    this.ticking = true;
    requestAnimationFrame(() => {
      this.applyPointerTransform(maxTranslate);
      this.ticking = false;
    });
  }

  applyPointerTransform(maxTranslate) {
    if (!this.desktopMedia) return;
    const rect = this.getBoundingClientRect();
    if (rect.width === 0 || rect.height === 0) return;

    const relX = (this.pointerX - rect.left) / rect.width - 0.5;
    const relY = (this.pointerY - rect.top) / rect.height - 0.5;

    const x = (relX * 2 * maxTranslate).toFixed(2);
    const y = (relY * 2 * maxTranslate).toFixed(2);

    this.desktopMedia.style.setProperty('--hero-parallax-x', `${x}px`);
    this.desktopMedia.style.setProperty('--hero-parallax-y', `${y}px`);
  }

  setupScroll() {
    const target = this.mobileMedia || this.desktopMedia;
    if (!target) return;
    target.classList.add('hero-parallax__media--active');

    const maxTranslate = 6; // px, single-axis so a touch more room than pointer mode

    this.onScroll = () => {
      if (this.ticking) return;
      this.ticking = true;
      requestAnimationFrame(() => {
        this.applyScrollTransform(target, maxTranslate);
        this.ticking = false;
      });
    };

    this.observer = new IntersectionObserver((entries) => {
      const isIntersecting = entries[0].isIntersecting;
      if (isIntersecting) {
        window.addEventListener('scroll', this.onScroll, { passive: true });
        this.applyScrollTransform(target, maxTranslate);
      } else {
        window.removeEventListener('scroll', this.onScroll);
      }
    });

    this.observer.observe(this);
  }

  applyScrollTransform(target, maxTranslate) {
    const rect = this.getBoundingClientRect();
    const viewportHeight = window.innerHeight;
    const denominator = viewportHeight / 2 + rect.height / 2;
    if (denominator === 0) return;

    const progress = (rect.top + rect.height / 2 - viewportHeight / 2) / denominator;
    const clampedProgress = Math.max(-1, Math.min(1, progress));
    const y = (clampedProgress * maxTranslate).toFixed(2);

    target.style.setProperty('--hero-parallax-y', `${y}px`);
  }
}

customElements.define('hero-parallax', HeroParallax);
